import { useMutation } from '@apollo/client/react'
import { CREATE_COUPON_ORDER_MUTATION } from 'api/symfony/graphql/mutations/createCouponOrder'
import axios from 'axios'
import { useState } from 'react'

import { parseCreateCouponOrderResponse } from 'api/symfony/parsers/graphqlParsers'

import {
  Coupon,
  CreateCouponOrderPlacementInput,
  Mutation,
  Studio,
} from '@definitions/types/symfonyTypes'

type callbackSuccessType = (status: string) => void
type callbackErrorType = () => void

interface useHandleCouponOrderReturnType {
  handleCouponOrder: (
    coupon: Coupon,
    affiliation: string,
    studio: Studio,
    email: string,
    newsletter: boolean
  ) => Promise<void>
  handleCouponOrderError: Error | null
}

const useHandleCouponOrder = (
  onCompleteCallback: callbackSuccessType | null,
  onErrorCallback: callbackErrorType | null
): useHandleCouponOrderReturnType => {
  const [handleCouponOrderError, setHandleCouponOrderError] =
    useState<Error | null>(null)

  const [createCouponOrderMutation] = useMutation<Mutation>(
    CREATE_COUPON_ORDER_MUTATION
  )

  const handleCouponOrder = async (
    coupon: Coupon,
    affiliation: string,
    studio: Studio,
    email: string,
    newsletter: boolean
  ): Promise<void> => {
    const input: CreateCouponOrderPlacementInput = {
      coupon: coupon.id,
      affiliation: affiliation,
      studio: studio.id,
      customerEmail: email,
    }

    try {
      const { data } = await createCouponOrderMutation({
        variables: {
          input,
        },
      })

      if (
        data &&
        'createCouponOrderPlacement' in data &&
        data.createCouponOrderPlacement &&
        onCompleteCallback
      ) {
        const parsed = parseCreateCouponOrderResponse(
          data.createCouponOrderPlacement
        )
        onCompleteCallback(parsed.status ?? '')
      }
    } catch (error) {
      const err = error as Error
      setHandleCouponOrderError(err)
      if (onErrorCallback) {
        onErrorCallback()
      }
      console.log(err)
    }

    if (newsletter) {
      try {
        await axios.post(
          '/api/subscribeMailchimp',
          { list: affiliation.toLowerCase(), email },
          { headers: { 'Content-Type': 'application/json' } }
        )
      } catch (error) {
        // optional newsletter subscription should not cause the whole coupon order to fail
      }
    }
  }

  return {
    handleCouponOrder,
    handleCouponOrderError,
  }
}

export default useHandleCouponOrder
